// src/User/ExpedientesUsuario.js — Solponiente
import React, { useEffect, useState } from "react";
import { Card, Table, Spinner, Badge, InputGroup, Form, Button, Container, Modal } from "react-bootstrap";
import Header from "../Components/Header";
import 'bootstrap-icons/font/bootstrap-icons.css';

const BASE_URL = "https://oficinavirtual-solponiente.com/api";

const ExpedientesUsuario = () => {
  const [expedientes, setExpedientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("");
  const [showDetalle, setShowDetalle] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [csvBusqueda, setCsvBusqueda] = useState("");
  const [descargandoCSV, setDescargandoCSV] = useState(false);
  const [errorCSV, setErrorCSV] = useState("");
  const userId = localStorage.getItem("id");
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchExpedientes = async () => {
      try {
        const res = await fetch(`${BASE_URL}/expedientes/usuario/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setExpedientes(await res.json());
      } catch (e) {
        console.error("Error al obtener expedientes:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchExpedientes();
  }, [userId]);

  const verDetalle = async (expedienteId) => {
    setLoadingDetalle(true);
    setShowDetalle(true);
    try {
      const res = await fetch(`${BASE_URL}/expedientes/${expedienteId}/detalle`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setDetalle(await res.json());
    } catch (e) {
      console.error("Error al obtener detalle:", e);
    } finally {
      setLoadingDetalle(false);
    }
  };

  const descargarDocumento = async (documentoId, nombreDocumento) => {
    try {
      const res = await fetch(`${BASE_URL}/documentos/${documentoId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = nombreDocumento || "documento.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      alert("Error al descargar el documento.");
    }
  };

  const descargarPorCSV = async () => {
    setDescargandoCSV(true);
    setErrorCSV("");
    try {
      const res = await fetch(`${BASE_URL}/verificar/${csvBusqueda}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setErrorCSV("No se encontró ningún documento con ese CSV.");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "documento.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      setErrorCSV("Error al conectar con el servidor.");
    } finally {
      setDescargandoCSV(false);
    }
  };

  const renderBadge = (estado) => {
    const map = {
      "en proceso": ["primary", "En proceso"],
      completada: ["success", "Completada"],
      rechazada: ["danger", "Rechazada"],
      pendiente: ["warning", "Pendiente"],
    };
    const [bg, label] = map[estado] || ["secondary", estado];
    return <Badge bg={bg}>{label}</Badge>;
  };

  const renderBadgeTipo = (tipo) => {
    const labels = { normal: "Normal", subsanacion: "Subsanación", justificante: "Justificante" };
    if (tipo === "justificante") {
      return (
        <Badge bg="white" text="primary" className="border border-primary">
          {labels.justificante}
        </Badge>
      );
    }
    const map = { normal: "secondary", subsanacion: "warning" };
    return <Badge bg={map[tipo] || "secondary"}>{labels[tipo] || tipo}</Badge>;
  };

  const filtrados = expedientes.filter((e) =>
    e.NumeroExpediente?.toLowerCase().includes(filtro.toLowerCase())
  );

  return (
    <div className="d-flex flex-column">
      <Header />
      <main className="flex-grow-1">
        <Container fluid className="py-3 px-4">
          <h4 className="section-title mb-2">
            <i className="bi bi-folder2-open text-primary me-2"></i>
            Mis Expedientes
          </h4>
          <p className="text-muted mb-4">
            Aquí puede consultar el estado de sus expedientes y verificar su CSV.
          </p>

          <Card className="shadow-sm bg-white" style={{ borderRadius: "0.75rem" }}>
            <Card.Body className="pb-0">
              <InputGroup className="mb-3" style={{ maxWidth: 400 }}>
                <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
                <Form.Control
                  placeholder="Buscar por número de expediente…"
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                />
                {filtro && (
                  <Button variant="outline-secondary" onClick={() => setFiltro("")}>✕</Button>
                )}
              </InputGroup>
            </Card.Body>

            {loading ? (
              <Card.Body className="text-center py-5">
                <Spinner animation="border" />
                <p className="mt-3">Cargando expedientes…</p>
              </Card.Body>
            ) : filtrados.length === 0 ? (
              <Card.Body>
                <p className="text-muted text-center py-4">
                  {filtro ? "No se encontraron resultados." : "No tiene expedientes abiertos."}
                </p>
              </Card.Body>
            ) : (
              <Card.Body className="p-1 pt-0">
                <Table hover responsive className="align-middle mb-0">
                  <thead className="thead-brand thead-label">
                    <tr>
                      <th>Nº Expediente</th>
                      <th>Solicitud</th>
                      <th>Descripción</th>
                      <th>Fecha de apertura</th>
                      <th>Estado</th>
                      <th>Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtrados.map((exp) => (
                      <tr key={exp.ExpedienteID}>
                        <td>
                          <code className="text-primary fw-bold fs-6">{exp.NumeroExpediente}</code>
                        </td>
                        <td><code className="small text-muted">{exp.NumeroRegistro || "—"}</code></td>
                        <td className="text-muted"><small>{exp.Descripcion}</small></td>
                        <td>
                          <small>
                            {exp.FechaCreacion
                              ? new Date(exp.FechaCreacion).toLocaleDateString("es-ES", {
                                  year: "numeric", month: "long", day: "numeric",
                                })
                              : "—"}
                          </small>
                        </td>
                        <td>{renderBadge(exp.Estado)}</td>
                        <td>
                          <Button size="sm" variant="outline-primary" onClick={() => verDetalle(exp.ExpedienteID)}>
                            <i className="bi bi-folder2-open me-1"></i> Ver detalle
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            )}
          </Card>

          {/* Buscador por CSV */}
          <Card className="shadow-sm bg-white mt-4" style={{ borderRadius: "0.75rem" }}>
            <Card.Body>
              <h5 className="mb-1">
                <i className="bi bi-patch-check text-primary me-2"></i>
                Descargar documento por CSV
              </h5>
              <p className="text-muted small mb-3">
                Introduzca el Código Seguro de Verificación para descargar el documento original.
              </p>
              <InputGroup style={{ maxWidth: 500 }}>
                <Form.Control
                  placeholder="Ej: A1B2C3D4E5F6G7H8"
                  value={csvBusqueda}
                  onChange={(e) => { setCsvBusqueda(e.target.value.toUpperCase()); setErrorCSV(""); }}
                  style={{ fontFamily: "monospace", letterSpacing: "0.05em" }}
                  maxLength={16}
                />
                <Button
                  variant="primary"
                  disabled={csvBusqueda.length !== 16 || descargandoCSV}
                  onClick={descargarPorCSV}
                >
                  {descargandoCSV ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <><i className="bi bi-download me-1"></i>Descargar</>
                  )}
                </Button>
              </InputGroup>
              {errorCSV && <p className="text-danger small mt-2">{errorCSV}</p>}
            </Card.Body>
          </Card>
        </Container>
      </main>

      {/* Modal detalle */}
      <Modal show={showDetalle} onHide={() => { setShowDetalle(false); setDetalle(null); }} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-folder2-open me-2 text-primary"></i>
            Detalle del Expediente
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDetalle ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-3">Cargando detalle...</p>
            </div>
          ) : detalle ? (
            <>
              <div className="p-3 mb-4 rounded" style={{ backgroundColor: "#eef4fb" }}>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="text-muted small mb-1">Número de expediente</div>
                    <code className="text-primary fw-bold fs-5">{detalle.NumeroExpediente}</code>
                    <div className="mt-1 text-muted small">
                      Abierto el {new Date(detalle.FechaCreacion).toLocaleDateString("es-ES", {
                        year: "numeric", month: "long", day: "numeric",
                      })}
                    </div>
                  </div>
                  {renderBadge(detalle.Estado)}
                </div>
                {detalle.Descripcion && (
                  <div className="mt-2 text-muted small">{detalle.Descripcion}</div>
                )}
              </div>

              <h6 className="fw-bold mb-3">
                <i className="bi bi-file-earmark-text me-2 text-primary"></i>
                Documentos aportados
              </h6>
              {detalle.Documentos?.length === 0 ? (
                <p className="text-muted small">No hay documentos.</p>
              ) : (
                <Table hover size="sm" className="mb-4">
                  <thead className="thead-brand thead-label">
                    <tr>
                      <th>Nombre</th>
                      <th>Fecha</th>
                      <th>Tipo</th>
                      <th>Descargar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.Documentos?.map((doc) => (
                      <tr key={doc.DocumentoID}>
                        <td className="small">{doc.NombreDocumento}</td>
                        <td className="small">{doc.FechaSubida}</td>
                        <td>
                          <Badge bg={doc.Tipo === "Solicitud inicial" ? "primary" : "warning"}>
                            {doc.Tipo}
                          </Badge>
                        </td>
                        <td>
                          <Button size="sm" variant="outline-primary" onClick={() => descargarDocumento(doc.DocumentoID, doc.NombreDocumento)}>
                            <i className="bi bi-download"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              <h6 className="fw-bold mb-3">
                <i className="bi bi-bell me-2 text-primary"></i>
                Notificaciones
              </h6>
              {detalle.Notificaciones?.length === 0 ? (
                <p className="text-muted small">No hay notificaciones.</p>
              ) : (
                <Table hover size="sm">
                  <thead className="thead-brand thead-label">
                    <tr>
                      <th>Asunto</th>
                      <th>Tipo</th>
                      <th>Fecha</th>
                      <th>CSV</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.Notificaciones?.map((n) => (
                      <tr key={n.NotificacionID}>
                        <td className="small">{n.Asunto || "—"}</td>
                        <td>{renderBadgeTipo(n.Tipo)}</td>
                        <td className="small">{n.FechaEnvio}</td>
                        <td>
                          {n.DocumentoCSV ? (
                            <code className="small text-primary fw-bold">{n.DocumentoCSV}</code>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            <Badge bg={n.Firmado ? "success" : "warning"}>
                              {n.Firmado ? "Firmada" : "Pendiente"}
                            </Badge>
                            {n.Tipo === "subsanacion" && (
                              <Badge bg={n.Subsanada ? "success" : "secondary"}>
                                {n.Subsanada ? "Subsanada " : "Pendiente de subsanar"}
                              </Badge>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </>
          ) : (
            <p className="text-muted">No se pudo cargar el detalle.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowDetalle(false); setDetalle(null); }}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ExpedientesUsuario;
